import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { CAMADAS } from '../../../styles/camadas';
import { styledDs as styled } from '../../../styles/theme';

const FOCAVEIS =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Fundo = styled.div<{ $camada: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $camada }) => $camada};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(11, 11, 11, 0.4);
  padding: ${({ theme }) => theme.space.lg};
`;

const Caixa = styled.div<{ $largura: string }>`
  width: min(${({ $largura }) => $largura}, 100%);
  max-height: 100%;
  overflow: auto;
  font-family: ${({ theme }) => theme.font.family};

  &:focus {
    outline: none;
  }
`;

export type DsModalProps = {
  open: boolean;
  /** Chamado no Escape, no clique no fundo e por quem fecha de dentro. */
  onClose: () => void;
  /** id do elemento que dá nome ao diálogo. Use um ou `ariaLabel`. */
  labelledBy?: string;
  ariaLabel?: string;
  largura?: string;
  /** Camada de empilhamento; confirmações precisam ficar acima de outro modal. */
  camada?: number;
  children: React.ReactNode;
};

/**
 * Diálogo modal com o mínimo que um modal precisa para não abandonar quem
 * navega por teclado: o foco entra ao abrir, o Tab circula dentro dele em vez
 * de passear pela página atrás, o Escape fecha e o foco volta para o botão que
 * abriu. Toda janela sobreposta da interface passa por aqui.
 */
export const DsModal: React.FC<DsModalProps> = ({
  open,
  onClose,
  labelledBy,
  ariaLabel,
  largura = '420px',
  camada = CAMADAS.overlay,
  children,
}) => {
  const caixa = useRef<HTMLDivElement>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    focoAnterior.current = document.activeElement as HTMLElement | null;

    const primeiro = caixa.current?.querySelector<HTMLElement>(FOCAVEIS);
    (primeiro ?? caixa.current)?.focus();

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        onClose();
        return;
      }

      if (evento.key !== 'Tab' || !caixa.current) return;

      const alvos = Array.from(caixa.current.querySelectorAll<HTMLElement>(FOCAVEIS));

      if (alvos.length === 0) {
        evento.preventDefault();
        return;
      }

      const inicio = alvos[0];
      const fim = alvos[alvos.length - 1];
      const atual = document.activeElement;

      // Sem isto o Tab escapa para a página de trás, que continua no fluxo.
      if (evento.shiftKey && (atual === inicio || atual === caixa.current)) {
        evento.preventDefault();
        fim.focus();
      } else if (!evento.shiftKey && atual === fim) {
        evento.preventDefault();
        inicio.focus();
      }
    }

    document.addEventListener('keydown', aoTeclar);

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
      focoAnterior.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <Fundo $camada={camada} onClick={onClose}>
      <Caixa
        ref={caixa}
        $largura={largura}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : ariaLabel}
        tabIndex={-1}
        onClick={(evento) => evento.stopPropagation()}
      >
        {children}
      </Caixa>
    </Fundo>,
    document.body
  );
};
